import { useEffect, useMemo, useState } from 'react';

import { faFileExcel } from '@fortawesome/pro-light-svg-icons';
import { useYearQuarterNavigation } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useRecoilState } from 'recoil';

import { Quantity, QuantityName } from './types';

import { CAPPLAN_TABS } from '../enums';

import { GroupBy, Quantities } from './constants';

import { groupByKey } from '../utils';
import { getTotals, ObjectKeys } from './utils';

import {
  downloadReport,
  getAnalyzerData
} from 'RestAPIs/capacity-plan/analyzer';

import { scenarioAtom } from '../atoms';
import {
  compareWithAtom,
  groupByAtom,
  initialVisibleQuantitiesAtom,
  quantitiesAtom,
  selectedCategoriesAtom,
  selectedFunctionsAtom,
  visibleQuantitiesAtom
} from './atoms';

import {
  Box,
  FormControlLabel,
  FormGroup,
  IconButton,
  styled,
  Switch,
  Tooltip
} from '@mui/material';

import { FontAwesomeSvgIcon, LoadingOverlay } from 'components/shared';

import NoScenario from '../components/NoScenario';
import Chart from './components/Chart';
import SettingsSection from './components/SettingsSection/SettingsSection';
import Table from './components/Table';

import Styles from './Styles';

const StyledFormGroup = styled(FormGroup)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 0 12px;
  margin: 0 auto;
`;

type Props = {
  showDetailsSubTab: (tab: keyof typeof CAPPLAN_TABS) => void;
};

const Analyzer = ({ showDetailsSubTab }: Props) => {
  const { t } = useTranslation('common');
  const [quantities, setQuantities] = useRecoilState(quantitiesAtom);
  const [defaultQuantities, setDefaultQuantities] = useState<Quantity[]>([]);
  const [visibleQuantities, setVisibleQuantities] = useRecoilState(
    visibleQuantitiesAtom
  );
  const [initialVisibleQuantities, setInitialVisibleQuantities] =
    useRecoilState(initialVisibleQuantitiesAtom);

  const [scenario] = useRecoilState(scenarioAtom);
  const [groupBy] = useRecoilState(groupByAtom);
  const [selectedCategories] = useRecoilState(selectedCategoriesAtom);
  const [compareWith] = useRecoilState(compareWithAtom);
  const [selectedFunctions] = useRecoilState(selectedFunctionsAtom);

  const { year, aggregate, interval } = useYearQuarterNavigation();

  const { isLoading, refetch } = useQuery(
    ['analyzerData', scenario?.scenarioId, year, aggregate],
    async ({ signal }) => {
      if (!scenario?.scenarioId) {
        return Promise.reject(new Error('No scenario ID'));
      }

      const { scenarioId } = scenario;
      const data = await getAnalyzerData({
        scenarioId,
        year,
        aggregateOn: aggregate,
        signal
      });

      const categories = ObjectKeys(data);
      const values = Object.values(data);

      if (values[0].length === 0) {
        setVisibleQuantities([Quantities.FORMATION, Quantities.ROSTER]);
        setInitialVisibleQuantities([Quantities.FORMATION, Quantities.ROSTER]);
      } else {
        setVisibleQuantities([Quantities.WORK_DEMAND, Quantities.ROSTER]);
        setInitialVisibleQuantities([
          Quantities.WORK_DEMAND,
          Quantities.ROSTER
        ]);
      }

      const quantities = values.map((value, i) => ({
        name: categories[i],
        scenarioId,
        data: value,
        totals: getTotals(value, aggregate)
      }));

      const defaultQuantities = quantities.map((quantity) => {
        let { data } = quantity;
        data = groupByKey(data, 'openDuty', t).sort(
          (a, b) => b.openDuty - a.openDuty
        );
        return {
          ...quantity,
          data
        };
      });

      setQuantities(quantities);
      setDefaultQuantities(defaultQuantities);
    },
    {
      enabled: Boolean(scenario?.scenarioId)
    }
  );

  useEffect(() => {
    if (scenario) {
      refetch();
    }
  }, [scenario, aggregate, year]);

  const quantitiesByFilters = useMemo(() => {
    const filtersActive =
      !!selectedCategories.length ||
      !!selectedFunctions.length ||
      !!compareWith ||
      !!groupBy;
    const quantitiesToFilter = filtersActive ? quantities : defaultQuantities;

    const filteredQuantities = quantitiesToFilter
      .filter(({ name }) => visibleQuantities.includes(name)) // filter by quantity
      .map((quantity) => {
        let { data } = quantity;

        // filter by categories or filters
        if (selectedCategories.length || selectedFunctions.length) {
          data = data.filter(
            ({
              category = t('unknown'),
              functionDescription = t('unknown')
            }) => {
              const hasCategoriesFilter = selectedCategories.length;
              const hasFunctionsFilter = selectedFunctions.length;

              if (hasCategoriesFilter && hasFunctionsFilter) {
                return (
                  selectedCategories.find((o) => o.name === category) &&
                  selectedFunctions.find((o) => o.name === functionDescription)
                );
              }
              if (hasCategoriesFilter) {
                return selectedCategories.find((o) => o.name === category);
              }
              if (hasFunctionsFilter) {
                return selectedFunctions.find(
                  (o) => o.name === functionDescription
                );
              }

              return true;
            }
          );
        }

        // filter by grouping
        if (groupBy) {
          const key = groupBy === GroupBy.CATEGORY ? 'category' : 'functionId';
          data = groupByKey(data, key, t);
        }

        data = data.map((entry) => ({
          ...entry,
          periods: entry.periods.slice(
            interval.start,
            interval.end || entry.periods.length
          )
        }));

        let totals = getTotals(data, aggregate);

        if (compareWith && quantity.name !== compareWith) {
          const compareQuantity = quantities.filter(
            (q) => q.name === compareWith
          )[0];

          totals = totals.map((q, i) => ({
            ...q,
            total: q.total - compareQuantity.totals[i].total
          }));
        }

        return {
          ...quantity,
          data,
          totals
        };
      });

    return filteredQuantities;
  }, [
    quantities,
    defaultQuantities,
    interval,
    visibleQuantities,
    groupBy,
    selectedCategories,
    selectedFunctions,
    compareWith,
    aggregate
  ]);

  const toggleQuantity = (quantity: QuantityName) => {
    const hasQuantity = visibleQuantities.includes(quantity);

    setVisibleQuantities(
      hasQuantity
        ? visibleQuantities.filter((q) => q !== quantity)
        : [...visibleQuantities, quantity]
    );

    // Remember user toggled settings for setting back the old values after mutations caused by compare with select
    setInitialVisibleQuantities(
      hasQuantity
        ? initialVisibleQuantities.filter((q) => q !== quantity)
        : [...initialVisibleQuantities, quantity]
    );
  };

  useEffect(() => {
    setVisibleQuantities(
      initialVisibleQuantities.filter((q) => q !== compareWith)
    );
  }, [compareWith]);

  const nonGroupByFiltersActive =
    !!selectedCategories.length || !!selectedFunctions.length || !!compareWith;

  const onDownloadClick = async () => {
    if (scenario?.scenarioId) {
      downloadReport({
        scenarioId: scenario.scenarioId,
        year,
        aggregateOn: aggregate
      });
    }
  };

  if (!scenario) {
    return <NoScenario />;
  }

  return (
    <Styles>
      <LoadingOverlay loading={isLoading} />
      <Box mb={4} position="relative" display="flex" alignItems="center">
        <Box display="flex" alignItems="center" ml="auto" gap={1}>
          <SettingsSection />
          <Tooltip title="Download rapport">
            <IconButton onClick={onDownloadClick}>
              <FontAwesomeSvgIcon icon={faFileExcel} color="primary" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <div className="d-flex" style={{ marginBottom: 20 }}>
        <div style={{ width: 92 }} />
        <div className="main">
          <Chart
            quantities={quantitiesByFilters}
            shouldStack={!!groupBy || !nonGroupByFiltersActive}
            isLoading={isLoading}
          />
          <StyledFormGroup sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch size="small" color={!groupBy ? 'dark' : 'default'} />
              }
              label={`${t('analyzer.filters.workDemand')}`}
              checked={visibleQuantities.includes(Quantities.WORK_DEMAND)}
              disabled={compareWith === Quantities.WORK_DEMAND}
              onChange={() => {
                toggleQuantity(Quantities.WORK_DEMAND);
              }}
            />
            <FormControlLabel
              control={
                <Switch size="small" color={!groupBy ? 'blue' : 'default'} />
              }
              label={`${t('analyzer.filters.basePlan')}`}
              checked={visibleQuantities.includes(Quantities.BASE_PLAN)}
              disabled={compareWith === Quantities.BASE_PLAN}
              onChange={() => {
                toggleQuantity(Quantities.BASE_PLAN);
              }}
            />
            <FormControlLabel
              control={
                <Switch size="small" color={!groupBy ? 'yellow' : 'default'} />
              }
              label={`${t('analyzer.filters.formation')}`}
              checked={visibleQuantities.includes(Quantities.FORMATION)}
              onChange={() => {
                toggleQuantity(Quantities.FORMATION);
              }}
            />
            <FormControlLabel
              control={
                <Switch size="small" color={!groupBy ? 'primary' : 'default'} />
              }
              label={`${t('analyzer.filters.roster')}`}
              checked={visibleQuantities.includes(Quantities.ROSTER)}
              disabled={compareWith === Quantities.ROSTER}
              onChange={() => {
                toggleQuantity(Quantities.ROSTER);
              }}
            />
          </StyledFormGroup>
        </div>
      </div>
      <Table
        quantities={quantitiesByFilters}
        showDetailsSubTab={showDetailsSubTab}
      />
    </Styles>
  );
};

export default Analyzer;
